import { SidebarTrigger } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { ArrowLeft, X } from 'lucide-react'
import React, { type ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { IconTip } from '@/components/icon-tip'
import { useFloatingHeader } from '@/hooks/use-floating-header'
import { useTitleInset } from '@/hooks/use-title-inset'
import { useHeaderStore } from '@/store/header.store'

const IOS_URL = 'https://apps.apple.com/app/id0000000000'
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=ai.goaltree.app'

function getAppUrl(): string | null {
  if (typeof navigator === 'undefined') return null
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return IOS_URL
  if (/Android/.test(ua)) return ANDROID_URL
  return null
}

export function OpenAppButton({ hidden }: { hidden?: boolean }) {
  const { t } = useTranslation()
  const url = getAppUrl()
  // TODO: remove this when the app is actually available
  const forceHidden = true
  if (!url || hidden || forceHidden) return null

  return (
    <span
      title={t('nav.comingSoon')}
      className={cn(
        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex cursor-not-allowed items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1 text-xs font-semibold text-foreground/40 backdrop-blur transition-[opacity] duration-200',
        hidden && 'pointer-events-none opacity-0',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        className="size-4 shrink-0 opacity-40"
      >
        <rect width="32" height="32" rx="8" fill="#a8ff3e" />
        <path
          d="M9 16l4.5 4.5L23 11"
          stroke="#0a0a0a"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      {t('nav.openApp')}
    </span>
  )
}

export function HeaderSubtitle({ text }: { text: string }) {
  return (
    <span className="mt-0.5 max-w-[50%] truncate text-[11px] leading-none text-muted-foreground/80 tabular-nums">
      {text}
    </span>
  )
}

interface SiteHeaderProps {
  leftSlot?: ReactNode
  rightSlot?: ReactNode
  showBack?: boolean
  onBack?: () => void
  showClose?: boolean
  onClose?: () => void
  className?: string
  transparent?: boolean
  // Float over the content without the light-on-media styling (defaults to `transparent`).
  overlay?: boolean
}

export function SiteHeader({
  leftSlot,
  rightSlot,
  showBack,
  onBack,
  showClose,
  onClose,
  className,
  transparent,
  overlay = transparent,
}: SiteHeaderProps) {
  const { t } = useTranslation()
  const { title, subtitle, scrolled, raised } = useHeaderStore()
  const headerRef = useRef<HTMLElement>(null)
  useFloatingHeader(headerRef, '--app-hdr-d', !overlay)
  const showTitle = scrolled && Boolean(title)
  const titleInset = useTitleInset(headerRef, showTitle)
  const router = useRouter()
  const navigate = useNavigate()

  function handleBack(fallback: string) {
    const idx = (router.history.location.state as unknown as Record<string, unknown> | null)?.idx
    if (typeof idx === 'number' && idx > 0) {
      router.history.back()
    } else {
      navigate({ to: fallback as '/' })
    }
  }

  return (
    <header
      ref={headerRef}
      data-raised={!overlay && raised ? '' : undefined}
      className={cn(
        'absolute inset-x-0 top-0 z-20 flex h-12 items-center px-4',
        overlay ? 'bg-transparent' : 'app-hdr',
        className,
      )}
      style={
        transparent
          ? ({
              '--foreground': 'oklch(1 0 0)',
              '--muted-foreground': 'oklch(0.8 0 0)',
            } as React.CSSProperties)
          : undefined
      }
    >
      {showBack ? (
        <IconTip label={t('common.back')}>
          <button
            data-hdr-side
            type="button"
            onClick={() => (onBack ? onBack() : handleBack('/goals'))}
            className={cn(
              '-ml-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors',
              transparent
                ? 'hover:bg-white/10 hover:text-white'
                : 'hover:bg-muted hover:text-foreground',
            )}
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        </IconTip>
      ) : showClose ? (
        <IconTip label={t('common.close')}>
          <button
            data-hdr-side
            type="button"
            onClick={() => (onClose ? onClose() : handleBack('/today'))}
            className={cn(
              '-ml-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors',
              transparent
                ? 'hover:bg-white/10 hover:text-white'
                : 'hover:bg-muted hover:text-foreground',
            )}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </IconTip>
      ) : (
        <SidebarTrigger data-hdr-side className="-ml-1 cursor-pointer" />
      )}
      {leftSlot && (
        <div data-hdr-side className="ml-2 flex min-w-0 flex-1 items-center overflow-hidden">
          {leftSlot}
        </div>
      )}
      <OpenAppButton hidden={showTitle} />
      <div
        className="pointer-events-none absolute inset-y-0 flex flex-col items-center justify-center select-none"
        style={{
          left: titleInset.left,
          right: titleInset.right,
          opacity: showTitle ? 1 : 0,
          transform: showTitle ? 'translateY(0)' : 'translateY(4px)',
          transition: 'opacity 0.2s, transform 0.2s',
        }}
      >
        <span className="max-w-full truncate text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </span>
        {subtitle && <HeaderSubtitle text={subtitle} />}
      </div>
      {rightSlot && (
        <div data-hdr-side className="ml-auto flex shrink-0 items-center gap-2">
          {rightSlot}
        </div>
      )}
    </header>
  )
}
